import "./App.css";
import { Route, Routes, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useCallback } from "react";
import MixedCodeResponse from "./MixedCodeResponse";
import ScrollToBottomButton from "./ScrollToBottomButton";
import AppStoreDownload from "./AppStoreDownload";
import "./ReloadButton.css";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import MyAccountModal from "./MyAccountModal";
import AppleCallback from "./AppleCallback";
import TextInputWithButton from "./TextInputWithButton";
import EmailVerificationResult from "./EmailVerificationResult";
import SubscriptionSuccess from './SubscriptionSuccess';
import SubscriptionCancel from './SubscriptionCancel';
import SubscriptionButton from './SubscriptionButton';
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { configureAppleSignIn, loadAppleSignInScript } from "./appleSignIn";

// Endpoints for authentication, etc.
const LOGIN_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/login";
const SIGNUP_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/signup";
const RESEND_VERIFICATION_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/resend-verification";
const APPLE_WEB_START_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/apple-signin-web/start";
const APPLE_WEB_EXCHANGE_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/apple-signin-web/exchange";
const APPLE_WEB_STATE_STORAGE_KEY = "developerAiAppleWebState";
const APPLE_WEB_STATE_EXPIRY_STORAGE_KEY = "developerAiAppleWebStateExpiresAt";

const CODE_GEN_WEB_ENDPOINT = "https://developer-ai.com/devaix/o-code-gen-ai-web-1";
const MIRROR_SESSION_QR_ENDPOINT = "https://developer-ai.com/devaix/mirror-session-qr";
const SESSION_TEXT_FETCH_URL_PREFIX = "https://developer-ai.com/devaix/mirror-session-fetch?session_key=";
const AUTO_LOGIN_ENDPOINT = "https://developer-ai.com/devaix/dev_ai_login/auto-login";

const devaiappiourl = "https://apps.apple.com/us/app/developer-ai/id6502913351";
const stripeSubPriceId1 = 'price_1R4XGiLS1T1y5uFxQqrU4qU7';


function Header({ user, subscription, onManageSubscription, onLoginClick, onAccountClick }) {
  return (
    <header className="site-header">
      <a className="site-brand" href="/" aria-label="Developer AI home">
        <span className="site-brand-logo"><img src="/developer_ai_logo.png" alt="" /></span>
        <span>Developer AI</span>
      </a>
      <nav className="site-nav-links" aria-label="Primary navigation">
        <a href="#workspace">Workspace</a>
        <a href={devaiappiourl} target="_blank" rel="noopener noreferrer">iOS app</a>
      </nav>
      <div className="site-nav-actions">
      {user ? (
        <>
          <button type="button" className="button button-secondary" onClick={onAccountClick}>
            My Account
          </button>
          <SubscriptionButton
            priceId={stripeSubPriceId1}
            user={user}
            subscription={subscription}
            onManageSubscription={onManageSubscription}
            className="button button-primary nav-subscription-button"
          />
        </>
      ) : (
        <>
          <button type="button" className="button button-secondary" onClick={onLoginClick}>Sign in</button>
          <a className="button button-primary" href={devaiappiourl} target="_blank" rel="noopener noreferrer">
            Get the app
          </a>
        </>
      )}
      </div>
    </header>
  );
}

function VisitorLanding({ onLoginClick }) {
  return (
    <main className="site-main visitor-main">
      <section className="visitor-hero" aria-labelledby="visitor-heading">
        <div className="visitor-copy">
          <p className="eyebrow"><span className="eyebrow-dot" />AI coding workspace</p>
          <h1 id="visitor-heading">Turn an idea into <span>working code.</span></h1>
          <p className="hero-description">
            Generate, debug and understand code in one focused workspace, then continue seamlessly in the Developer AI app on iPhone.
          </p>
          <div className="hero-actions">
            <button type="button" className="button button-primary button-large" onClick={onLoginClick}>Open Developer AI</button>
            <a className="button button-secondary button-large" href={devaiappiourl} target="_blank" rel="noopener noreferrer">Download for iPhone</a>
          </div>
          <div className="feature-line" aria-label="Key features">
            <span>Code generation</span>
            <span>Cross-device handoff</span>
            <span>Syntax-aware output</span>
          </div>
        </div>

        <section className="code-preview" aria-label="Example Developer AI response">
          <div className="code-preview-bar">
            <span className="window-dot" /><span className="window-dot" /><span className="window-dot" />
            <span className="code-preview-title">sort_numbers.py</span>
          </div>
          <pre className="code-preview-body"><code><span className="syntax-purple">def</span> <span className="syntax-blue">bubble_sort</span>(values):{"\n"}
    <span className="syntax-purple">for</span> end <span className="syntax-purple">in</span> range(len(values) - <span className="syntax-green">1</span>, <span className="syntax-green">0</span>, -<span className="syntax-green">1</span>):{"\n"}
        swapped = <span className="syntax-purple">False</span>{"\n"}
        <span className="syntax-purple">for</span> index <span className="syntax-purple">in</span> range(end):{"\n"}
            <span className="syntax-muted"># Compare adjacent items</span>{"\n"}
            <span className="syntax-purple">if</span> values[index] &gt; values[index + <span className="syntax-green">1</span>]:{"\n"}
                values[index], values[index + <span className="syntax-green">1</span>] = ...</code></pre>
          <div className="ai-note">
            <strong>Developer AI</strong>
            <span>I added an early-exit check so already sorted input finishes in a single pass.</span>
          </div>
        </section>
      </section>

      <section className="how-it-works" id="how-it-works" aria-labelledby="how-it-works-title">
        <div>
          <p className="eyebrow"><span className="eyebrow-dot" />One connected workflow</p>
          <h2 id="how-it-works-title">Write on the web. Pick up on iPhone.</h2>
        </div>
        <div className="workflow-steps">
          <article><span>1</span><strong>Ask</strong><p>Describe what you want to create or fix.</p></article>
          <article><span>2</span><strong>Review</strong><p>Read and copy a clear, syntax-aware response.</p></article>
          <article><span>3</span><strong>Continue</strong><p>Scan once to keep the conversation moving on your iPhone.</p></article>
        </div>
      </section>
    </main>
  );
}

// Pass user and subscription props to AutoRefresh so it can decide whether to render the QR block.
const AutoRefresh = ({ user, subscription, onLoginClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [lastGenerationPrompt, setLastGenerationPrompt] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [sessionKey, setSessionKey] = useState(null);

  useEffect(() => {
    if (user) return;

    setData(null);
    setQrData(null);
    setSessionKey(null);
    setSessionError(null);
    setGenerationError(null);
    setLastGenerationPrompt("");
  }, [user]);

  // Fetch the QR code data from the /mirror-session-qr endpoint.
  useEffect(() => {
    if (!user || !subscription) return; // Only fetch QR data when user and subscription are available
  
    const fetchQrData = async () => {
      try {
        const response = await fetch(MIRROR_SESSION_QR_ENDPOINT, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const qrResult = await response.json();
        setQrData(qrResult);
        if (qrResult.qr_text) {
          setSessionKey(qrResult.qr_text);
        }
      } catch (e) {
        console.error("Error fetching QR data:", e);
      }
    };
  
    fetchQrData();
  }, [user, subscription]);
  

  // Fetch session text from the mirror-session-fetch endpoint using the latest sessionKey.
  useEffect(() => {
     // Skip if prerequisites are not met
    if (!user || !subscription || !sessionKey) {
      setLoading(false); // explicitly mark loading as done
      return;
    }
  
    const fetchData2 = async () => {
      try {
        let accessToken = localStorage.getItem("accessToken");
        const devurl2 = `${SESSION_TEXT_FETCH_URL_PREFIX}${encodeURIComponent(sessionKey)}`;
        const response = await fetch(devurl2, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${accessToken}`,
          },
        });
    
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
    
        const jsonResult = await response.json();
        const sessionText = jsonResult?.session_text || "";
        setData(prevData =>
          sessionText === prevData ? prevData : sessionText
        );
        setSessionError(null);
    
      } catch (error) {
        setSessionError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    // Immediately fetch and then set an interval
    fetchData2();
    const intervalId = setInterval(() => {
      fetchData2();
      console.log("Refreshed at:", new Date().toLocaleTimeString());
    }, 10000);
  
    return () => clearInterval(intervalId);
  }, [sessionKey, user, subscription]);
  
  const requestGeneration = async (text) => {
    const accessToken = localStorage.getItem("accessToken");
    setIsFetching(true);
    setGenerationError(null);
    try {
      const payload = {
        session_key: sessionKey,
        input_text: text,
      };
      const response = await fetch(CODE_GEN_WEB_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success || !result?.message) {
        throw new Error(
          result?.message || result?.error || `Generation failed (HTTP ${response.status}).`
        );
      }
      setData(result.message);
    } catch (e) {
      console.error("Error during POST fetch:", e);
      setGenerationError(e.message || "Couldn’t generate a response. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleTextSubmit = (text) => {
    const prompt = text.trim();
    if (!prompt || isFetching) return;
    setLastGenerationPrompt(prompt);
    requestGeneration(prompt);
  };

  const retryGeneration = () => {
    if (!lastGenerationPrompt || isFetching) return;
    requestGeneration(lastGenerationPrompt);
  };
  

  if (!user) return <VisitorLanding onLoginClick={onLoginClick} />;

  const hasWorkspaceAccess = Boolean(
    subscription && subscription.status && !["none", "expired"].includes(String(subscription.status).toLowerCase())
  );
  const subscriptionLabel = hasWorkspaceAccess ? "Subscription active" : "Subscription needed";
  const starterPrompts = [
    "Create a small Python script",
    "Explain this error",
    "Help me plan a feature",
  ];

  return (
    <main className="site-main workspace-main" id="workspace">
      <section className="workspace-heading">
        <div>
          <p className="eyebrow"><span className="eyebrow-dot" />Developer AI workspace</p>
          <h1>Your coding workspace</h1>
          <p>Generate code here, then continue the session in Developer AI on your iPhone.</p>
        </div>
        <p className={`subscription-chip${hasWorkspaceAccess ? " is-active" : ""}`}>
          <span />{subscriptionLabel}
        </p>
      </section>

      <div className="workspace-grid">
        <section className="chat-panel" aria-label="Developer AI coding conversation">
          <div className="chat-panel-heading">
            <div className="assistant-identity">
              <span className="assistant-avatar" aria-hidden="true">✦</span>
              <div><strong>Developer AI</strong><span>Code assistant</span></div>
            </div>
            <span className={`assistant-readiness${sessionKey ? " is-ready" : ""}`}><i />{sessionKey ? "Ready" : "Connecting"}</span>
          </div>
          <div className={`chat-content${data !== null ? " has-response" : ""}`}>
            {loading && <div className="workspace-loading"><span className="spinner" />Preparing your session…</div>}
            {!loading && !hasWorkspaceAccess && (
              <div className="empty-state"><strong>Subscription required</strong><p>Start a subscription from the menu above to use the web workspace.</p></div>
            )}
            {!loading && hasWorkspaceAccess && !data && !generationError && (
              <div className="empty-state assistant-empty-state">
                <span className="assistant-avatar large" aria-hidden="true">✦</span>
                <strong>What would you like to build?</strong>
                <p>Ask for code, debugging help, an explanation, or a practical starting point.</p>
                <div className="starter-prompts" aria-label="Suggested requests">
                  {starterPrompts.map((prompt) => (
                    <button key={prompt} type="button" onClick={() => handleTextSubmit(prompt)} disabled={!sessionKey || isFetching}>{prompt}</button>
                  ))}
                </div>
              </div>
            )}
            {isFetching && <div className="workspace-loading"><span className="spinner" />Creating your response…</div>}
            {generationError && (
              <div className="generation-error" role="alert">
                <strong>Couldn’t generate a response</strong><p>{generationError}</p>
                <button type="button" className="text-action" onClick={retryGeneration} disabled={isFetching || !lastGenerationPrompt}>Try again</button>
              </div>
            )}
            {sessionError && <p className="session-error" role="status">Couldn’t refresh the linked app session. We’ll keep trying in the background.</p>}
            {lastGenerationPrompt && hasWorkspaceAccess && (
              <article className="user-request"><span>You</span><p>{lastGenerationPrompt}</p></article>
            )}
            {data !== null && hasWorkspaceAccess && (
              <article className="assistant-response">
                <div className="assistant-response-meta">
                  <div className="assistant-identity">
                    <span className="assistant-avatar" aria-hidden="true">✦</span>
                    <div><strong>Developer AI</strong><span>Latest response</span></div>
                  </div>
                  <span>RESPONSE</span>
                </div>
                <div className="code2"><MixedCodeResponse response={data} /></div>
                <ScrollToBottomButton />
              </article>
            )}
          </div>
          <div className="composer-shell">
            {hasWorkspaceAccess && sessionKey ? (
              <TextInputWithButton onSubmit={handleTextSubmit} disabled={isFetching} />
            ) : (
              <p>{hasWorkspaceAccess ? "Preparing your linked iPhone session…" : "A subscription is needed before you can send a request."}</p>
            )}
          </div>
        </section>

        <aside className="workspace-side-stack">
          <section className="side-panel" id="iphone">
            <div className="side-panel-title"><strong>Connect your iPhone</strong><span className={sessionKey ? "active-label" : ""}>{sessionKey ? "READY" : "WAITING"}</span></div>
            <p>Scan once in the Developer AI app to continue this session on your phone.</p>
            {hasWorkspaceAccess && qrData?.qr_image ? (
              <img className="qr-code" src={qrData.qr_image} alt="QR code for connecting the Developer AI app" />
            ) : (
              <div className="qr-empty" aria-hidden="true"><span>QR</span></div>
            )}
            <div className="side-panel-footer"><span>{sessionKey ? "Code ready to scan" : "Waiting for a session"}</span><a href={devaiappiourl} target="_blank" rel="noopener noreferrer">Get the app</a></div>
          </section>

          <section className="side-panel plan-panel">
            <div className="side-panel-title"><strong>Your account</strong><span className={hasWorkspaceAccess ? "active-label" : ""}>{hasWorkspaceAccess ? "ACTIVE" : "SET UP"}</span></div>
            <p>{hasWorkspaceAccess ? "Web generation and iPhone handoff are ready when you are." : "Choose a subscription to unlock web generation and iPhone handoff."}</p>
            <div className="side-panel-footer"><span>Managed securely</span><a href="#workspace">View account</a></div>
          </section>
        </aside>
      </div>

      <section className="app-download-strip">
        <div><strong>Take Developer AI with you.</strong><p>Use the iPhone app to keep working wherever you are.</p></div>
        <AppStoreDownload appUrl={devaiappiourl} />
      </section>
    </main>
  );
};

function Home({ user, subscription, onLoginClick }) {
  return <AutoRefresh user={user} subscription={subscription} onLoginClick={onLoginClick} />;
}

function App() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [appleLoginReady, setAppleLoginReady] = useState(false);

  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const scheduleTokenRefreshRef = useRef(null);
  const currentUserId = user?.id ?? null;

  const handleLogout = () => {
    setUser(null);
    setSubscription(null);
    localStorage.removeItem("userEmail");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    setShowAccountModal(false);
  };

  const scheduleTokenRefresh = (accessToken) => {
    try {
      const decoded = jwtDecode(accessToken);
      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const delay = expiryTime - currentTime - 60000;
      if (delay > 0) {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => { refreshAccessToken(); }, delay);
        console.log("Scheduled token refresh in", delay / 1000, "seconds");
      }
    } catch (error) {
      console.error("Failed to schedule token refresh:", error);
    }
  };
  scheduleTokenRefreshRef.current = scheduleTokenRefresh;

  const verifyToken = async (token) => {
    try {
      const response = await fetch("https://developer-ai.com/devaix/dev_ai_login/verify-token", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        console.log("Token verified by server.");
        return true;
      } else {
        console.log("Server verification failed. Refreshing token.");
        await refreshAccessToken();
        return false;
      }
    } catch (error) {
      console.error("Error verifying token:", error);
      return false;
    }
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    try {
      const response = await fetch("https://developer-ai.com/devaix/dev_ai_login/refresh-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const result = await response.json();
      if (result.success) {
        localStorage.setItem("accessToken", result.access_token);
        scheduleTokenRefresh(result.access_token);
        return result.access_token;
      } else {
        handleLogout();
        return null;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      handleLogout();
      return null;
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const valid = await verifyToken(token);
        if (!valid) await refreshAccessToken();
        else {
          try {
            const decoded = jwtDecode(token);
            const expiryTime = decoded.exp * 1000;
            const currentTime = Date.now();
            if (expiryTime < currentTime + 60000) await refreshAccessToken();
            else scheduleTokenRefresh(token);
          } catch (error) {
            console.error("Error decoding token:", error);
            await refreshAccessToken();
          }
        }
      }
    };
    checkToken();
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem("userEmail");
    const savedUserId = localStorage.getItem("userId");
    if (savedEmail && savedUserId) {
      setUser({ email: savedEmail, id: savedUserId });
    }
  }, []);
  
  const handleEmailLogin = async (email, password) => {
    try {
      const response = await fetch(LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
  
      const result = await response.json();
      if (result.success && result.is_verified) {
        localStorage.setItem("accessToken", result.access_token);
        localStorage.setItem("refreshToken", result.refresh_token);
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userId", result.user_id);
  
        setUser({ email, id: result.user_id, is_verified: result.is_verified });
        scheduleTokenRefresh(result.access_token);
        setShowLoginModal(false);
        return result;
      } else {
        return result;
      }
    } catch (error) {
      alert("Login error: " + error.toString());
      return { success: false, error: error.toString() };
    }
  };
  
  const fetchProtectedData = async (url) => {
    let accessToken = localStorage.getItem("accessToken");
    try {
      let response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
      });
      
      if (response.status === 401) {
        await refreshAccessToken();
        accessToken = localStorage.getItem("accessToken");
        response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
        });
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching protected data:", error);
      return null;
    }
  };

  const handleEmailSignUp = async (email, password) => {
    try {
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const result = await response.json();
      if (result.success) return { success: true };
      else return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: error.toString() };
    }
  };
  
  const prepareAppleLogin = useCallback(async () => {
    setAppleLoginReady(false);
    const response = await fetch(APPLE_WEB_START_ENDPOINT, {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.success || !result.state || !result.nonce) {
      throw new Error(result?.error || "Apple Sign-In is unavailable.");
    }

    await loadAppleSignInScript();
    configureAppleSignIn({ state: result.state, nonce: result.nonce, usePopup: false });
    sessionStorage.setItem(APPLE_WEB_STATE_STORAGE_KEY, result.state);
    sessionStorage.setItem(
      APPLE_WEB_STATE_EXPIRY_STORAGE_KEY,
      String(Date.now() + (Number(result.expires_in) || 600) * 1000)
    );
    setAppleLoginReady(true);
  }, []);

  const completeAppleLogin = useCallback(async ({ exchangeCode, state }) => {
    const expectedState = sessionStorage.getItem(APPLE_WEB_STATE_STORAGE_KEY);
    if (!expectedState || !state || state !== expectedState) {
      throw new Error("This Apple sign-in request could not be verified. Please try again.");
    }

    const response = await fetch(APPLE_WEB_EXCHANGE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ exchange_code: exchangeCode }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.success || !result.access_token || !result.refresh_token || !result.email || !result.user_id) {
      throw new Error(result?.error || "Apple Sign-In could not be completed. Please try again.");
    }

    localStorage.setItem("accessToken", result.access_token);
    localStorage.setItem("refreshToken", result.refresh_token);
    localStorage.setItem("userEmail", result.email);
    localStorage.setItem("userId", result.user_id);
    sessionStorage.removeItem(APPLE_WEB_STATE_STORAGE_KEY);
    sessionStorage.removeItem(APPLE_WEB_STATE_EXPIRY_STORAGE_KEY);

    setUser({ email: result.email, id: result.user_id, is_verified: result.is_verified });
    scheduleTokenRefreshRef.current?.(result.access_token);
    setShowLoginModal(false);
    setShowSignUpModal(false);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    prepareAppleLogin().catch((error) => {
      console.error("Unable to prepare Apple Sign-In:", error);
      setAppleLoginReady(false);
    });
  }, [prepareAppleLogin]);

  const handleAppleLogin = async () => {
    const expiresAt = Number(sessionStorage.getItem(APPLE_WEB_STATE_EXPIRY_STORAGE_KEY));
    if (!appleLoginReady || !window.AppleID?.auth || !expiresAt || Date.now() >= expiresAt) {
      try {
        await prepareAppleLogin();
      } catch (error) {
        console.error("Unable to prepare Apple Sign-In:", error);
        alert("Apple Sign-In is currently unavailable. Please use an alternative login method.");
        return;
      }
    }

    try {
      // `usePopup: false` navigates the current page to Apple rather than opening a new window.
      // The registered callback returns to /apple-callback, which calls completeAppleLogin.
      await window.AppleID.auth.signIn();
    } catch (error) {
      if (error?.error === "user_cancelled_authorize") {
        return;
      }
      console.error("Apple Sign-In could not be started:", error);
      alert(error.message || "Apple Sign-In is currently unavailable. Please try again.");
    }
  };

  useEffect(() => {
    async function fetchSubscription() {
      const userId = currentUserId;
      const accessToken = localStorage.getItem("accessToken");
      if (!userId || !accessToken) {
        setSubscription(null);
        return;
      }
      try {
        const response = await fetch(
          `https://developer-ai.com/devaix/api/users/${userId}/subscription`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        } else {
          console.error("Failed to fetch subscription status");
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    }
    fetchSubscription();
    const intervalId = setInterval(fetchSubscription, 30000);
    return () => clearInterval(intervalId);
  }, [currentUserId]);

  const handleManageSubscription = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(
        `${backendUrl}/api/create-customer-portal-session`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const portalUrl = response.data.url;
      if (portalUrl) window.location.href = portalUrl;
      else alert("Unable to retrieve the subscription management portal.");
    } catch (error) {
      console.error("Error creating customer portal session:", error);
      alert("There was an error managing your subscription. Please try again later.");
    }
  };

  const handleAutoLogin = async (autoLoginToken) => {
    try {
      const response = await fetch(AUTO_LOGIN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auto_login_token: autoLoginToken }),
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem("accessToken", data.access_token);
        localStorage.setItem("refreshToken", data.refresh_token);
        localStorage.setItem("userEmail", data.email);
        localStorage.setItem("userId", data.user_id);
        setUser({ email: data.email, id: data.user_id });
        console.log("Auto login successful!");
        navigate("/");
      } else {
        console.error("Auto login failed:", data.error);
      }
    } catch (error) {
      console.error("Auto login error:", error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const autoLoginToken = params.get("auto_login_token");
    if (autoLoginToken) {
      handleAutoLogin(autoLoginToken);
    }
  }, []);

  return (
    <div className="App">
      <Header
        user={user}
        subscription={subscription}
        onManageSubscription={handleManageSubscription}
        onLoginClick={() => setShowLoginModal(true)}
        onAccountClick={() => setShowAccountModal(true)}
      />
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onEmailLogin={handleEmailLogin}
          onAppleLogin={handleAppleLogin}
          onSwitchToSignUp={() => {
            setShowLoginModal(false);
            setShowSignUpModal(true);
          }}
          RESEND_VERIFICATION_ENDPOINT={RESEND_VERIFICATION_ENDPOINT}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={() => setShowSignUpModal(false)}
          onEmailSignUp={handleEmailSignUp}
          onSwitchToLogin={() => {
            setShowSignUpModal(false);
            setShowLoginModal(true);
          }}
          onAppleLogin={handleAppleLogin}
          onEmailLogin={handleEmailLogin}
          RESEND_VERIFICATION_ENDPOINT={RESEND_VERIFICATION_ENDPOINT}
        />
      )}
      {showAccountModal && user && (
        <MyAccountModal
          user={user}
          onClose={() => setShowAccountModal(false)}
          onLogout={handleLogout}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={<Home user={user} subscription={subscription} onLoginClick={() => setShowLoginModal(true)} />}
        />
        <Route path="/apple-callback" element={<AppleCallback onComplete={completeAppleLogin} />} />
        <Route path="/email-verification-result" element={<EmailVerificationResult />} />
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
      </Routes>
    </div>
  );
}

export default App;
